/**
 * Business Capability Map Widget (issue #1653, part of epic #1650)
 *
 * Shows the capability hierarchy (bounded to the widget's documented
 * `depth=2`, matching `homepage-dashboard-spec.md`'s "Business Capability
 * Map" `?depth=2` example — L1+L2) as a nested tree, with each capability's
 * SUPPORTS-edge service count and a gap indicator for capabilities with no
 * supporting services. Follows the single-query + `useStatisticsWidget`
 * pattern shared by `DependencyHealthWidget`.
 */

import { useCapabilityMap, type CapabilityMapNode } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './CapabilityMapWidget.css';

/** Matches the spec's documented `?depth=2` example (L1+L2). */
const CAPABILITY_MAP_DEPTH = 2;

const WIDGET_CONFIG: WidgetConfig = {
  id: 'capability-map',
  title: 'Business Capability Map',
  size: 'large',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface CapabilityMapStats {
  capabilities: CapabilityMapNode[];
  gapCount: number;
}

type CapabilityMapQueryData = ReturnType<typeof useCapabilityMap>['data'];

function extractCapabilityMapStats(data: CapabilityMapQueryData): CapabilityMapStats | null {
  if (!data?.capabilityMap) {
    return null;
  }
  return data.capabilityMap;
}

function CapabilityMapNodeItem({ node }: { node: CapabilityMapNode }): JSX.Element {
  const isGap = node.serviceCount === 0;
  return (
    <li className="capability-map-widget__node" data-testid="capability-map-node">
      <div
        className={`capability-map-widget__node-row${
          isGap ? ' capability-map-widget__node-row--gap' : ''
        }`}
      >
        <span className="capability-map-widget__node-name">{node.name}</span>
        <span className="capability-map-widget__node-count">{node.serviceCount} services</span>
      </div>
      {node.children.length > 0 && (
        <ul className="capability-map-widget__children">
          {node.children.map((child) => (
            <CapabilityMapNodeItem key={child.id} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function CapabilityMapWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useCapabilityMap(CAPABILITY_MAP_DEPTH),
    extractCapabilityMapStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.capabilities.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="capability-map-widget__empty">
          <IconElement name="account_tree" className="capability-map-widget__empty-icon" />
          <p className="forge-typography--body2">No capability data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="capability-map-widget__content">
        {stats.gapCount > 0 && (
          <p
            className="capability-map-widget__gap-summary forge-typography--caption"
            data-testid="capability-map-gap-summary"
          >
            <IconElement
              name="warning"
              className="capability-map-widget__gap-icon"
              aria-hidden="true"
            />
            {stats.gapCount} capabilit{stats.gapCount === 1 ? 'y' : 'ies'} with no supporting
            services
          </p>
        )}
        <ul className="capability-map-widget__tree" data-testid="capability-map-tree">
          {stats.capabilities.map((node) => (
            <CapabilityMapNodeItem key={node.id} node={node} />
          ))}
        </ul>
      </div>
    </DashboardWidget>
  );
}
